import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, useEffect } from 'react';
import { subMonths, format, startOfMonth, endOfMonth } from 'date-fns';
import { getCurrentUserId, supabase } from '../lib/supabase';
import { supabaseAccountService } from '../services/supabase-service';
// transactionService import removed - currently unused
import { AccountType } from '../types/account.types';
import { calculateCurrentBalance as calcBalance } from '../utils/balance-calculator';
import type { Transaction } from '../types/transaction.types';

export interface DashboardStats {
  monthlySavings: {
    value: number;
    transactionCount?: number;
    change?: number;
    changeType?: 'positive' | 'negative' | 'neutral';
  };
  monthlyIncome: {
    value: number;
    transactionCount?: number;
    change?: number;
    changeType?: 'positive' | 'negative' | 'neutral';
  };
  monthlyExpenses: {
    value: number;
    transactionCount?: number;
    change?: number;
    changeType?: 'positive' | 'negative' | 'neutral';
  };
  netWorth: {
    value: number;
    change?: number;
    changeType?: 'positive' | 'negative' | 'neutral';
  };
}

function calculatePercentageChange(current: number, previous: number): number | undefined {
  if (previous === 0) {
    return current === 0 ? undefined : (current > 0 ? 100 : -100);
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

function getChangeType(change?: number): 'positive' | 'negative' | 'neutral' {
  if (change === undefined) return 'neutral';
  if (change > 0) return 'positive';
  if (change < 0) return 'negative';
  return 'neutral';
}

/**
 * Fetch all transactions for an account and calculate its balance
 */
async function getAccountCalculatedBalance(
  userId: string,
  accountId: string,
  openingBalance: number,
  openingDate: string,
  accountType: AccountType
): Promise<number> {
  try {
    // Fetch regular transactions
    const { data: regularTransactions } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .eq('account_id', accountId)
      .is('archived_at', null)
      .order('date', { ascending: true })
      .limit(10000);

    // Fetch incoming transfers (where this account is the destination)
    const { data: incomingTransfers } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', userId)
      .eq('destination_account_id', accountId)
      .eq('type', 'transfer')
      .is('archived_at', null)
      .order('date', { ascending: true })
      .limit(10000);

    // Combine and deduplicate transactions
    const allTransactions: Transaction[] = [];
    const seenIds = new Set<string>();

    [...(regularTransactions || []), ...(incomingTransfers || [])].forEach((row) => {
      if (!seenIds.has(row.id)) {
        seenIds.add(row.id);
        allTransactions.push({
          id: row.id,
          userId: row.user_id ?? '',
          accountId: row.account_id ?? '',
          categoryId: row.category_id ?? undefined,
          amount: parseFloat(String(row.amount)) || 0,
          description: row.description ?? '',
          merchantName: row.merchant_name ?? undefined,
          date: row.date ?? '',
          type: (row.type ?? 'debit') as 'debit' | 'credit' | 'transfer',
          status: (row.status ?? 'pending') as 'completed' | 'pending' | 'needs_review',
          notes: row.notes ?? undefined,
          sourceAccountId: row.source_account_id ?? undefined,
          destinationAccountId: row.destination_account_id ?? undefined,
          createdAt: row.created_at ?? '',
          updatedAt: row.updated_at ?? '',
        });
      }
    });

    // Calculate balance using the utility function
    return calcBalance(openingBalance, openingDate, allTransactions, accountType, accountId);
  } catch (error) {
    console.error('Error calculating account balance:', error);
    return openingBalance;
  }
}

/**
 * Calculate Net Worth: Sum of all accounts with includeInNetWorth: true
 * Calculates balances from transactions (not stored current_balance)
 * Includes assets: sum of currentValue for assets with includeInNetWorth: true
 */
async function calculateNetWorth(userId: string): Promise<number> {
  const result = await supabaseAccountService.getAccounts(userId, { includeInNetWorth: true });
  
  // Separate assets from other accounts
  const assetAccounts = result.accounts.filter(acc => acc.type === 'assets');
  const nonAssetAccounts = result.accounts.filter(acc => acc.type !== 'assets');
  
  // Calculate balance for each non-asset account from transactions
  const balances = await Promise.all(
    nonAssetAccounts.map(async (acc) => {
      const calculatedBalance = await getAccountCalculatedBalance(
        userId,
        acc.id,
        acc.opening_balance || 0,
        acc.opening_date ?? '',
        acc.type as AccountType
      );
      return calculatedBalance;
    })
  );

  const accountBalance = balances.reduce((total, balance) => total + balance, 0);
  
  // Sum asset values (use currentValue, not calculated from transactions)
  const assetValue = assetAccounts.reduce((total, asset) => {
    const value = asset.current_value || 0;
    return total + (isNaN(value) ? 0 : value);
  }, 0);

  return accountBalance + assetValue;
}

/**
 * Get account IDs for Everyday and Liabilities accounts only
 */
async function getEverydayAndLiabilityAccountIds(userId: string): Promise<string[]> {
  const result = await supabaseAccountService.getAccounts(userId, { status: 'active' });
  
  return result.accounts
    .filter(acc => 
      acc.type === AccountType.BANK_CASH || 
      acc.type === AccountType.CREDIT_CARD || 
      acc.type === AccountType.LOAN
    )
    .map(acc => acc.id);
}

/**
 * Get account IDs for Savings accounts only
 */
async function getSavingsAccountIds(userId: string): Promise<string[]> {
  const result = await supabaseAccountService.getAccounts(userId, { status: 'active' });
  
  return result.accounts
    .filter(acc => acc.type === AccountType.SAVINGS)
    .map(acc => acc.id);
}

/**
 * Calculate total income for a specific month
 * Only includes transactions categorized as Income (category type = 'income')
 * Includes ALL account types (income can come from any account)
 * Fetches all transactions (no pagination limit) to ensure accuracy
 */
async function calculateMonthlyIncome(
  userId: string,
  dateFrom: string,
  dateTo: string
): Promise<{ amount: number; count: number }> {
  // Get ALL active account IDs (income can come from any account type)
  const result = await supabaseAccountService.getAccounts(userId, { status: 'active' });
  const accountIds = result.accounts.map(acc => acc.id);
  
  if (accountIds.length === 0) {
    return { amount: 0, count: 0 };
  }

  // Use direct Supabase query to get ALL transactions with their categories
  // Filter by category type = 'income' instead of transaction type = 'credit'
  // This ensures we only count transactions that are actually categorized as income
  const { data: transactions, error } = await supabase
    .from('transactions')
    .select(`
      account_id,
      amount,
      category_id,
      categories (
        type
      )
    `)
    .eq('user_id', userId)
    .not('category_id', 'is', null) // Only include categorized transactions
    .in('account_id', accountIds)
    .in('status', ['completed', 'pending']) // Only include completed and pending transactions
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .is('archived_at', null);

  if (error || !transactions) {
    console.error('Error fetching monthly income transactions:', error);
    return { amount: 0, count: 0 };
  }

  // Filter by category type field = 'income' (not category name)
  // This ensures we only count transactions categorized as income
  // The categories join returns an object (or array) with a 'type' field
  const incomeTransactions = transactions.filter(t => {
    // Handle both object and array cases (Supabase can return either)
    const category = Array.isArray(t.categories) 
      ? t.categories[0] 
      : (t.categories as { type?: string } | null);
    
    // Check if category exists and type is 'income'
    if (!category || !category.type) {
      return false;
    }
    
    // Normalize to lowercase for comparison (database stores lowercase)
    return category.type.toLowerCase() === 'income';
  });

  return {
    amount: incomeTransactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
    count: incomeTransactions.length
  };
}

/**
 * Calculate total expenses (debits) for a specific month
 * Only includes categorized transactions that aren't excluded from spending
 * Matches the logic used in getSpendingByCategory for consistency
 * Only includes Everyday (BANK_CASH) and Liabilities (CREDIT_CARD, LOAN) accounts
 */
async function calculateMonthlyExpenses(
  userId: string,
  dateFrom: string,
  dateTo: string
): Promise<{ amount: number; count: number }> {
  // Get account IDs for everyday and liability accounts only
  const accountIds = await getEverydayAndLiabilityAccountIds(userId);
  
  if (accountIds.length === 0) {
    return { amount: 0, count: 0 };
  }

  // Fetch transactions with category information
  const { data: transactions, error } = await supabase
    .from('transactions')
    .select(`
      amount,
      category_id,
      categories (
        id,
        exclude_from_spending,
        parent_id
      )
    `)
    .eq('user_id', userId)
    .eq('type', 'debit')
    .in('account_id', accountIds)
    .in('status', ['completed', 'pending']) // Match spending calculation
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .is('archived_at', null);

  if (error || !transactions) {
    console.error('Error fetching monthly expenses transactions:', error);
    return { amount: 0, count: 0 };
  }

  // Collect parent category IDs that need to be fetched
  const parentIdsToFetch = new Set<string>();
  const categoryDetails = new Map<string, { excludeFromSpending: boolean; parentId: string | null }>();
  
  for (const row of transactions || []) {
    const category = row.categories;
    if (category?.id && !categoryDetails.has(category.id)) {
      categoryDetails.set(category.id, {
        excludeFromSpending: category.exclude_from_spending ?? false,
        parentId: category.parent_id ?? null,
      });
      
      if (category.parent_id) {
        parentIdsToFetch.add(category.parent_id);
      }
    }
  }

  // Fetch parent categories if needed
  const parentCategoryDetails = new Map<string, boolean>();
  const missingParentIds = Array.from(parentIdsToFetch).filter((parentId) => !categoryDetails.has(parentId));
  
  if (missingParentIds.length > 0) {
    const { data: parentRows, error: parentError } = await supabase
      .from('categories')
      .select('id, exclude_from_spending')
      .or(`user_id.eq.${userId},is_system.eq.true`)
      .in('id', missingParentIds);

    if (parentError) {
      console.error('Failed to fetch parent categories for monthly expenses:', parentError);
    } else {
      for (const parent of parentRows || []) {
        if (parent?.id) {
          parentCategoryDetails.set(parent.id, parent.exclude_from_spending ?? false);
        }
      }
    }
  }

  // Filter out uncategorized and excluded categories
  const expenseTransactions = transactions.filter(t => {
    const category = t.categories;
    
    // Skip uncategorized transactions
    if (!category?.id) {
      return false;
    }
    
    const categoryInfo = categoryDetails.get(category.id);
    if (!categoryInfo) {
      return false;
    }
    
    // Check if category or parent category is excluded
    const isExcluded = 
      categoryInfo.excludeFromSpending === true ||
      (categoryInfo.parentId && parentCategoryDetails.get(categoryInfo.parentId) === true);
    
    // Skip excluded categories (matching spending calculation default behavior)
    return !isExcluded;
  });

  return {
    amount: expenseTransactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
    count: expenseTransactions.length
  };
}

/**
 * Calculate Monthly Savings (Income - Expenses) for Savings accounts only
 */
async function calculateMonthlySavings(
  userId: string,
  dateFrom: string,
  dateTo: string
): Promise<{ amount: number; count: number }> {
  const accountIds = await getSavingsAccountIds(userId);
  
  if (accountIds.length === 0) {
    return { amount: 0, count: 0 };
  }

  // Fetch all transactions for savings accounts
  const { data: transactions, error } = await supabase
    .from('transactions')
    .select('amount, type')
    .eq('user_id', userId)
    .in('account_id', accountIds)
    .in('status', ['completed', 'pending'])
    .gte('date', dateFrom)
    .lte('date', dateTo)
    .is('archived_at', null);

  if (error || !transactions) {
    console.error('Error fetching monthly savings transactions:', error);
    return { amount: 0, count: 0 };
  }

  // Calculate net flow: credits (income) minus debits (expenses)
  const netAmount = transactions.reduce((sum, t) => {
    const amount = Math.abs(t.amount || 0);
    if (t.type === 'credit') {
      return sum + amount; // Income
    } else if (t.type === 'debit') {
      return sum - amount; // Expenses
    }
    return sum; // Transfers don't affect savings
  }, 0);

  return {
    amount: netAmount,
    count: transactions.length
  };
}

/**
 * Dashboard Stats Hook
 * 
 * Hybrid approach:
 * - Monthly Savings: Net flow (income - expenses) for Savings accounts in selected month
 * - Monthly Income & Expenses: Show selected month totals
 * - Net Worth: Always show current values (calculated from transactions)
 * - Percentage changes: Compare to previous month
 */
export function useDashboardStats(selectedMonth?: Date) {
  // Get current user ID for cache key - updates when auth state changes
  const [userId, setUserId] = useState<string | null>(null);
  
  useEffect(() => {
    // Get initial user ID
    getCurrentUserId().then(setUserId);
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Calculate date ranges for selected month and the month before (for comparison)
  const dateRanges = useMemo(() => {
    const now = new Date();
    
    // Use selected month or default to last complete month
    const targetMonth = selectedMonth || subMonths(now, 1);
    const targetMonthStart = startOfMonth(targetMonth);
    const targetMonthEnd = endOfMonth(targetMonth);
    
    // Previous month (for comparison)
    const previousMonth = subMonths(targetMonth, 1);
    const previousMonthStart = startOfMonth(previousMonth);
    const previousMonthEnd = endOfMonth(previousMonth);
    
    return {
      selectedMonth: {
        from: format(targetMonthStart, 'yyyy-MM-dd'),
        to: format(targetMonthEnd, 'yyyy-MM-dd'),
      },
      previousMonth: {
        from: format(previousMonthStart, 'yyyy-MM-dd'),
        to: format(previousMonthEnd, 'yyyy-MM-dd'),
      },
    };
  }, [selectedMonth]);

  // Fetch current stats (selected month for income/expenses/savings, point-in-time for net worth)
  const { data: currentStats, isLoading: isLoadingCurrent } = useQuery({
    queryKey: ['dashboard-stats-current', userId, dateRanges.selectedMonth.from, dateRanges.selectedMonth.to],
    queryFn: async () => {
      const currentUserId = await getCurrentUserId();
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }

      const [netWorth, monthlyIncomeResult, monthlyExpensesResult, monthlySavingsResult] =
        await Promise.all([
          calculateNetWorth(currentUserId),
          calculateMonthlyIncome(currentUserId, dateRanges.selectedMonth.from, dateRanges.selectedMonth.to),
          calculateMonthlyExpenses(currentUserId, dateRanges.selectedMonth.from, dateRanges.selectedMonth.to),
          calculateMonthlySavings(currentUserId, dateRanges.selectedMonth.from, dateRanges.selectedMonth.to),
        ]);

      return {
        netWorth,
        monthlyIncome: monthlyIncomeResult.amount,
        monthlyIncomeCount: monthlyIncomeResult.count,
        monthlyExpenses: monthlyExpensesResult.amount,
        monthlyExpensesCount: monthlyExpensesResult.count,
        monthlySavings: monthlySavingsResult.amount,
        monthlySavingsCount: monthlySavingsResult.count,
      };
    },
    enabled: !!userId, // Only run when we have a user ID
  });

  // Fetch previous month stats for income/expenses/savings comparison
  const { data: previousStats, isLoading: isLoadingPrevious } = useQuery({
    queryKey: ['dashboard-stats-previous', userId, dateRanges.previousMonth.from, dateRanges.previousMonth.to],
    queryFn: async () => {
      const currentUserId = await getCurrentUserId();
      if (!currentUserId) {
        throw new Error('User not authenticated');
      }

      const [monthlyIncomeResult, monthlyExpensesResult, monthlySavingsResult] = await Promise.all([
        calculateMonthlyIncome(currentUserId, dateRanges.previousMonth.from, dateRanges.previousMonth.to),
        calculateMonthlyExpenses(currentUserId, dateRanges.previousMonth.from, dateRanges.previousMonth.to),
        calculateMonthlySavings(currentUserId, dateRanges.previousMonth.from, dateRanges.previousMonth.to),
      ]);

      return {
        monthlyIncome: monthlyIncomeResult.amount,
        monthlyExpenses: monthlyExpensesResult.amount,
        monthlySavings: monthlySavingsResult.amount,
      };
    },
    enabled: !!userId, // Only run when we have a user ID
  });

  const stats = useMemo((): DashboardStats | undefined => {
    if (!currentStats) return undefined;

    // Income: Compare selected month to previous month
    const monthlyIncomeChange = previousStats
      ? calculatePercentageChange(currentStats.monthlyIncome, previousStats.monthlyIncome)
      : undefined;

    // Expenses: Compare selected month to previous month
    const monthlyExpensesChange = previousStats
      ? calculatePercentageChange(currentStats.monthlyExpenses, previousStats.monthlyExpenses)
      : undefined;

    // For expenses, negative change is positive (spending less is good)
    const monthlyExpensesChangeType =
      monthlyExpensesChange !== undefined
        ? monthlyExpensesChange < 0
          ? 'positive'
          : monthlyExpensesChange > 0
          ? 'negative'
          : 'neutral'
        : 'neutral';

    // Savings: Compare selected month to previous month
    const monthlySavingsChange = previousStats
      ? calculatePercentageChange(currentStats.monthlySavings, previousStats.monthlySavings)
      : undefined;

    // For savings, positive change is good (saving more)
    const monthlySavingsChangeType =
      monthlySavingsChange !== undefined
        ? monthlySavingsChange > 0
          ? 'positive'
          : monthlySavingsChange < 0
          ? 'negative'
          : 'neutral'
        : 'neutral';

    return {
      monthlySavings: {
        value: currentStats.monthlySavings,
        transactionCount: currentStats.monthlySavingsCount,
        change: monthlySavingsChange,
        changeType: monthlySavingsChangeType,
      },
      monthlyIncome: {
        value: currentStats.monthlyIncome,
        transactionCount: currentStats.monthlyIncomeCount,
        change: monthlyIncomeChange,
        changeType: getChangeType(monthlyIncomeChange),
      },
      monthlyExpenses: {
        value: currentStats.monthlyExpenses,
        transactionCount: currentStats.monthlyExpensesCount,
        change: monthlyExpensesChange,
        changeType: monthlyExpensesChangeType,
      },
      netWorth: {
        value: currentStats.netWorth,
        // No percentage change for net worth (point-in-time, no historical comparison)
        change: undefined,
        changeType: 'neutral',
      },
    };
  }, [currentStats, previousStats]);

  return {
    stats,
    isLoading: isLoadingCurrent || isLoadingPrevious,
  };
}
